import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    AdminExpertListItemSchema,
    AdminExpertDetailSchema,
    AdminExpertFilterOptionsSchema,
    AdminExpertListMetaSchema,
    AdminExpertManagerStatsSchema,
    UpdateExpertStatusResponseSchema,
    UpdateExpertSlugResponseSchema,
    ExpertTagOptionSchema,
    ExpertFilterPresetSchema,
    ExpertNoteSchema,
    ExpertNoteListMetaSchema,
    SyncExpertTagsResponseSchema,
    type UpdateExpertStatusRequest,
    type UpdateExpertSlugRequest,
    type UpdateExpertDetailsRequest,
    type ExpertFilterPresetRequest,
    type SyncExpertTagsRequest,
    type CreateExpertNoteRequest,
} from "../schemas/expert-manager";

export interface AdminExpertManagerListQueryParams extends QueryParams {
    search?: string;
    status?: string;
    expert_type?: string;
    country?: string;
    service_category?: string;
    partner_tier?: string;
    language?: string;
    is_pro?: boolean;
    claim_status?: string;
    /** Match-all semantics: an expert must carry every listed tag. */
    tag_ids?: number[];
    /** Match-any semantics: an expert carrying any listed tag is left out. */
    exclude_tag_ids?: number[];
    limit?: number;
    cursor?: string;
}

export interface AdminExpertTagsQueryParams extends QueryParams {
    search?: string;
}

export interface AdminExpertNotesQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
}

export function createAdminExpertManagerApi(http: HttpClient, baseUrl: string) {
    const adminExpertsBaseUrl = `${baseUrl}/admin/expert-manager`;

    return {
        listExperts: createEndpointWithPagination<
            z.infer<typeof AdminExpertListItemSchema>,
            z.infer<typeof AdminExpertListMetaSchema>,
            unknown,
            AdminExpertManagerListQueryParams
        >(http, "GET", "", {
            base: adminExpertsBaseUrl,
            itemSchema: AdminExpertListItemSchema,
            metaSchema: AdminExpertListMetaSchema,
        }),

        getExpert: createEndpoint<
            z.infer<typeof AdminExpertDetailSchema>,
            unknown,
            QueryParams,
            { expertId: number | string }
        >(http, "GET", "/:expertId", {
            base: adminExpertsBaseUrl,
            responseSchema: AdminExpertDetailSchema,
            params: ["expertId"],
        }),

        getStats: createEndpoint<
            z.infer<typeof AdminExpertManagerStatsSchema>
        >(http, "GET", "/stats", {
            base: adminExpertsBaseUrl,
            responseSchema: AdminExpertManagerStatsSchema,
        }),

        getFilterOptions: createEndpoint<
            z.infer<typeof AdminExpertFilterOptionsSchema>
        >(http, "GET", "/filter-options", {
            base: adminExpertsBaseUrl,
            responseSchema: AdminExpertFilterOptionsSchema,
        }),

        activateApplication: createEndpoint<
            z.infer<typeof UpdateExpertStatusResponseSchema>,
            unknown,
            QueryParams,
            { expertId: number | string }
        >(http, "POST", "/:expertId/activate-application", {
            base: adminExpertsBaseUrl,
            responseSchema: UpdateExpertStatusResponseSchema,
            params: ["expertId"],
        }),

        updateStatus: createEndpoint<
            z.infer<typeof UpdateExpertStatusResponseSchema>,
            UpdateExpertStatusRequest,
            QueryParams,
            { expertId: number | string }
        >(http, "POST", "/:expertId/status", {
            base: adminExpertsBaseUrl,
            responseSchema: UpdateExpertStatusResponseSchema,
            params: ["expertId"],
        }),

        updateSlug: createEndpoint<
            z.infer<typeof UpdateExpertSlugResponseSchema>,
            UpdateExpertSlugRequest,
            QueryParams,
            { expertId: number | string }
        >(http, "PUT", "/:expertId/slug", {
            base: adminExpertsBaseUrl,
            responseSchema: UpdateExpertSlugResponseSchema,
            params: ["expertId"],
        }),

        updateFlags: createEndpoint<
            { expert_id: number; flags: string[] },
            { flags: string[] },
            QueryParams,
            { expertId: number | string }
        >(http, "POST", "/:expertId/flags", {
            base: adminExpertsBaseUrl,
            responseSchema: z.object({ expert_id: z.number(), flags: z.array(z.string()) }),
            params: ["expertId"],
        }),

        updateDetails: createEndpoint<
            z.infer<typeof AdminExpertDetailSchema>,
            UpdateExpertDetailsRequest,
            QueryParams,
            { expertId: number | string }
        >(http, "PATCH", "/:expertId/details", {
            base: adminExpertsBaseUrl,
            responseSchema: AdminExpertDetailSchema,
            params: ["expertId"],
        }),

        deleteExpert: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { expertId: number | string }
        >(http, "DELETE", "/:expertId", {
            base: adminExpertsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["expertId"],
        }),

        deleteApplication: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { expertId: number | string }
        >(http, "DELETE", "/:expertId/application", {
            base: adminExpertsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["expertId"],
        }),

        resendActivation: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { expertId: number | string }
        >(http, "POST", "/:expertId/resend-activation", {
            base: adminExpertsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["expertId"],
        }),

        listTags: createEndpoint<
            z.infer<typeof ExpertTagOptionSchema>[],
            unknown,
            AdminExpertTagsQueryParams
        >(http, "GET", "/tags", {
            base: adminExpertsBaseUrl,
            responseSchema: z.array(ExpertTagOptionSchema),
        }),

        syncExpertTags: createEndpoint<
            z.infer<typeof SyncExpertTagsResponseSchema>,
            SyncExpertTagsRequest,
            QueryParams,
            { expertId: number | string }
        >(http, "PUT", "/:expertId/tags", {
            base: adminExpertsBaseUrl,
            responseSchema: SyncExpertTagsResponseSchema,
            params: ["expertId"],
        }),

        listFilterPresets: createEndpoint<
            z.infer<typeof ExpertFilterPresetSchema>[]
        >(http, "GET", "/filter-presets", {
            base: adminExpertsBaseUrl,
            responseSchema: z.array(ExpertFilterPresetSchema),
        }),

        createFilterPreset: createEndpoint<
            z.infer<typeof ExpertFilterPresetSchema>,
            ExpertFilterPresetRequest
        >(http, "POST", "/filter-presets", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertFilterPresetSchema,
        }),

        getFilterPreset: createEndpoint<
            z.infer<typeof ExpertFilterPresetSchema>,
            unknown,
            QueryParams,
            { presetId: number | string }
        >(http, "GET", "/filter-presets/:presetId", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertFilterPresetSchema,
            params: ["presetId"],
        }),

        updateFilterPreset: createEndpoint<
            z.infer<typeof ExpertFilterPresetSchema>,
            ExpertFilterPresetRequest,
            QueryParams,
            { presetId: number | string }
        >(http, "PUT", "/filter-presets/:presetId", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertFilterPresetSchema,
            params: ["presetId"],
        }),

        deleteFilterPreset: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { presetId: number | string }
        >(http, "DELETE", "/filter-presets/:presetId", {
            base: adminExpertsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["presetId"],
        }),

        listExpertNotes: createEndpointWithPagination<
            z.infer<typeof ExpertNoteSchema>,
            z.infer<typeof ExpertNoteListMetaSchema>,
            unknown,
            AdminExpertNotesQueryParams,
            { expertId: number | string }
        >(http, "GET", "/:expertId/notes", {
            base: adminExpertsBaseUrl,
            itemSchema: ExpertNoteSchema,
            metaSchema: ExpertNoteListMetaSchema,
            params: ["expertId"],
        }),

        createExpertNote: createEndpoint<
            z.infer<typeof ExpertNoteSchema>,
            CreateExpertNoteRequest,
            QueryParams,
            { expertId: number | string }
        >(http, "POST", "/:expertId/notes", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertNoteSchema,
            params: ["expertId"],
        }),
    };
}
